import { Route, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { ROUTES, ROUTE_MAP } from "./RouteMaps";
import { IRouteConfig, NavConfigItem, RouteConfig } from "./RouteTypes";
import { Routes } from "./RouteNames";
import { buildRoute, isNullEmptyOrUndefined } from "@techsharegov/react-ui";
import { useEffect, useState } from "react";
import { DocumentTitle, SubRouteConfig, SubRoutes } from "Components/Navigation";
import { SUBROUTES_MAP, SUB_NAV_CONFIG_MAP } from "Navigation/SubRouteMaps";


//helper constants
export const ADD_EDIT = 'AddEdit';
export const VIEW = 'View';

export const useGetRoutes = () => {
  return Object.keys(ROUTES).map((routeName: string) => {
    var config = ROUTES[routeName];
    return (
      <Route
        path={config?.path}
        component={
          config?.component
        }
        exact={config?.isExact}
        key={routeName}
      />
    );
  });
};

export const useGetSubRoutes = (route: Routes) => {
  const routeMap = ROUTE_MAP[route];
  const routeConfig = ROUTES[route];  

  return routeMap
    ? Object.values(routeMap).map((subRouteConfig: IRouteConfig) => (
        <Route
          path={buildRoute(routeConfig.path, subRouteConfig.path)}
          component={subRouteConfig.component}
          key={subRouteConfig.path}
          exact={subRouteConfig.isExact}
        />
      ))
    : null;
};

export interface RouteMapperProps {
  route: Routes;
}

export const RouteMapper = ({ route }: RouteMapperProps) => {
  const subRoutes = useGetSubRoutes(route);

  return <>{subRoutes}</>;
};

export const useNavigation = (navItems: RouteConfig[]) => {
  const [navConfig, setNavConfig] = useState<RouteConfig[]>(navItems || []);
  const { pathname } = useLocation();
  let history = useHistory();

  useEffect(() => {
    var tempConfig: RouteConfig[] = [];
    var defaultRoute;
    if (pathname === '/') {
      tempConfig = navConfig.map((x: RouteConfig) => {
        x.active = x.path === Routes.Meetings || false;
        return x;
      });
      defaultRoute = tempConfig.find((x) => x.path === Routes.Meetings);
    } else {
      tempConfig = navConfig.map((x: RouteConfig) => {
        x.active = pathname.startsWith(x.path);
        return x;
      });
    }
    setNavConfig(
      tempConfig
        .map((x) => {
          return x;
        })
    );
    if (defaultRoute) {
      history.push(defaultRoute.path);
    }
  }, [pathname]);

  return { navConfig };
};



/*
 * @param route
 * Builds the full route path recursively from default subroutes
 */
export const getFullDefaultRoute = (
  route: string,
  ...parentRoutes: string[]
): string => {
  var fullRoute = parentRoutes?.length
    ? buildRoute(...parentRoutes, route)
    : route;
  var subRoutes = SUBROUTES_MAP[fullRoute];

  if (subRoutes !== undefined) {
    var defaultSubRoute =
      Object.values(subRoutes).find((x) => x.default) ||
      Object.values(subRoutes)[0];

    if (defaultSubRoute !== undefined) {
      return route + getFullDefaultRoute(defaultSubRoute.route, fullRoute);
    }
  }

  return route;
};

export const useGetSecureSubNavItems = (
  filter?: (x: NavConfigItem) => boolean,
  noPermissionsFilter = false
) => {

  const getSubNavItems = (parentRoute?: string) => {
    if (parentRoute) {
      return (
        SUB_NAV_CONFIG_MAP[parentRoute]
          ?.filter(filter || ((x) => x))
          .map((x) => {
            return {
              fullRoute: getFullDefaultRoute(x.route, parentRoute),
              ...x,
            };
          })
      );
    } else {
      return [];
    }
  };

  return getSubNavItems;
};

export const useSubNavigation = (parentRoute?: string) => {
  const getSubNavItems = useGetSecureSubNavItems();
  const navConfig = useActiveRouteSetter(() => {
    return getSubNavItems(parentRoute);
  });

  return navConfig;
};

export const useActiveRouteSetter = (
  getInitialValue: () => NavConfigItem[]
) => {
  const [navConfig, setNavConfig] = useState<NavConfigItem[]>(
    getInitialValue()
  );
  const { pathname } = useLocation();

  useEffect(() => {
    var tempConfig: NavConfigItem[] = getInitialValue();
    tempConfig = tempConfig.map((x: NavConfigItem) => {
      const routeParts = pathname
        .split('?')[0]
        .split('/')
        .filter((s) => !isNullEmptyOrUndefined(s));
      x.active = routeParts.includes(x.route.replace('/', ''));
      return x;
    });

    setNavConfig(tempConfig);
  }, [pathname]);
  return navConfig;
};

/*
  Returns function for mapping SubRoutes instance to individual Route Components,
  filters by permissions
*/
export const useSubRoutes = (
  filter = (x: [string, SubRouteConfig]) => true,
  renderDocumentTitle = (routeLabel: string) => routeLabel,
  nestRoutes = true
) => {
  let { url } = useRouteMatch();
  
  const mapSubRoutesToRoutes = (subRoutes: SubRoutes) => {
    
    return Object.entries(subRoutes)
      .filter(
        ([, subRoute]) =>
          subRoute.component !== undefined
      )
      .filter(filter)
      .map(([, subRoute], index) => {
        const { exact, ...otherRouteProps } = subRoute.routeProps;
        const Component = subRoute.component;

        return (
          <Route
            key={index}
            path={buildRoute(url, subRoute.path)}
            exact={exact !== undefined ? exact : true}
            render={
              renderDocumentTitle
                ? (props) => (
                    <>
                      <DocumentTitle
                        subTitle={renderDocumentTitle(subRoute.label)}
                        nestRoutes={nestRoutes}
                      />
                      <Component {...props} />
                    </>
                  )
                : (props) => <Component {...props} />
            }
            {...otherRouteProps}
          />
        );
      });
  };

  return mapSubRoutesToRoutes;
};

export function getSubRouteName(mainRoute: Routes | string, subRoute: string) {
  return mainRoute + subRoute;
}
